import {
  Row,
  Typography,
  Dropdown,
  MenuProps,
  Button,
  Modal,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { scenarioApi } from '@/api';
import DuplicateIcon from '@/assets/icons/icon-folder.svg?react';
import InfoIcon from '@/assets/icons/icon-info.svg?react';
import DeactivateIcon from '@/assets/icons/icon-power.svg?react';
import VerticalEllipsisIcon from '@/assets/icons/icon-three-dots-vertical.svg?react';
import ViewIcon from '@/assets/icons/icon-view-second.svg?react';
import RNotificationCard from '@/components/RNotificationCard';
import RTag from '@/components/RTag';
import CustomTooltip from '@/components/RTooltip';
import { TourSteps } from '@/constants';
import useProductTourStore from '@/store/useProductTourStore';
import { DrawerType } from '@/types/global';
import { Scenario } from '@/types/scenario';
import { capitalizeFirstLetter, handleError } from '@/utils';

import useProductTour from './useProductTour';

const useScenariosTableColumns = (
  mutate: () => void,
  updateDrawer: (
    type: DrawerType<Scenario>['type'],
    data: DrawerType<Scenario>['data']
  ) => void
) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const { currentStep, isTourActive } = useProductTourStore();
  const dropdownWrapperRef = useProductTour('viewDeactivateScenario');

  const handleActivate = (record: Scenario) => {
    return modal.confirm({
      closeIcon: false,
      footer: null,
      width: 475,
      modalRender: () => (
        <RNotificationCard
          title={t('scenario.notifications.activate.title', {
            name: capitalizeFirstLetter(record.name),
          })}
          message={t('scenario.notifications.activate.message')}
          type="warning"
          confirmText={t('common.yes')}
          cancelText={t('common.cancel')}
          onCancel={() => Modal.destroyAll()}
          open={!record.isActive}
          onConfirm={async () => {
            try {
              await scenarioApi.activateScenario(record.id);
              mutate();
              Modal.destroyAll();
              notification.success({
                message: t('scenario.notifications.activated', {
                  name: capitalizeFirstLetter(record.name),
                }),
              });
            } catch (error) {
              Modal.destroyAll();
              handleError(error);
            }
          }}
        />
      ),
    });
  };

  //TODO DESTROY ALL DISABLES MODAL CLOSE ANIMATION FIX IT LATER
  const handleDeactivate = async (record: Scenario) => {
    return modal.confirm({
      // TODO CHANGE THIS OPEN LOGIC
      closeIcon: false,
      footer: null,
      width: 475,
      modalRender: () => (
        <RNotificationCard
          title={t('scenario.notifications.deactivate.title', {
            name: capitalizeFirstLetter(record.name),
          })}
          message={t('scenario.notifications.deactivate.message')}
          type="warning"
          confirmText={t('common.yes')}
          cancelText={t('common.cancel')}
          open={record.isActive}
          onCancel={() => Modal.destroyAll()}
          onConfirm={async () => {
            try {
              await scenarioApi.deactiveScenario(record.id);
              mutate();
              Modal.destroyAll();
              notification.success({
                message: t('scenario.notifications.deactivated', {
                  name: capitalizeFirstLetter(record.name),
                }),
              });
            } catch (error) {
              Modal.destroyAll();
              handleError(error);
            }
          }}
        />
      ),
    });
  };

  const handleView = (record: Scenario) => {
    updateDrawer('view', record);
  };
  const handleDuplicate = (record: Scenario) => {
    updateDrawer('duplicate', record);
  };

  const generateItems = (record: Scenario) => {
    const items: MenuProps['items'] = [
      {
        label: t('scenario.table.view'),
        key: 'view-scenario',
        icon: <ViewIcon style={{ width: '16px', height: '16px' }} />,
        disabled: isTourActive,
      },
      {
        label: t('scenario.table.duplicate'),
        key: 'duplicate-scenario',
        icon: <DuplicateIcon style={{ width: '16px', height: '16px' }} />,
        disabled: isTourActive,
      },
    ];

    if (record.isActive) {
      items.push({
        label: t('scenario.table.deactivate'),
        key: 'deactivate-scenario',
        icon: <DeactivateIcon style={{ width: '16px', height: '16px' }} />,
        disabled: isTourActive,
      });
    } else {
      items.push({
        label: t('scenario.table.activate'),
        key: 'activate-scenario',
        icon: <DeactivateIcon style={{ width: '16px', height: '16px' }} />,
        disabled: isTourActive,
      });
    }

    return items;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMenuClick = (info: any, record: Scenario) => {
    switch (info.key) {
      case 'view-scenario':
        handleView(record);
        break;
      case 'duplicate-scenario':
        handleDuplicate(record);
        break;
      case 'deactivate-scenario':
        handleDeactivate(record);
        break;
      case 'activate-scenario':
        handleActivate(record);
        break;
      default:
        break;
    }
  };

  const columns: ColumnsType<Scenario> = [
    {
      title: t('scenario.table.name'),
      dataIndex: 'name',
      key: 'name',
      width: '%15',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name) => (
        <Row align="middle">
          <Typography.Paragraph
            style={{ maxWidth: '200px' }}
            className="text"
            ellipsis={{
              rows: 1,
              tooltip: name,
              expandable: false,
            }}
          >
            {name}
          </Typography.Paragraph>
        </Row>
      ),
    },
    {
      title: t('scenario.table.description'),
      dataIndex: 'description',
      key: 'description',
      width: '%25',
      render: (description) => (
        <Row align="middle">
          <Typography.Paragraph
            style={{ maxWidth: '200px' }}
            className="text"
            ellipsis={{
              rows: 1,
              tooltip: description,
              expandable: false,
            }}
          >
            {description}
          </Typography.Paragraph>
        </Row>
      ),
    },
    {
      title: (
        <CustomTooltip
          className="cursor-pointer"
          title={t('scenario.table.tooltip.pricesAndLoadTitle')}
          description={t('scenario.table.tooltip.pricesAndLoadDesc')}
          trigger="click"
        >
          <Row align="middle" justify="start" className="w-100">
            <Typography.Text className="pr-8">
              {t('scenario.table.pricesAndLoad')}
            </Typography.Text>
            <InfoIcon fill={theme.colors.dark} />
          </Row>
        </CustomTooltip>
      ),
      dataIndex: 'pricesAndLoad',
      key: 'pricesAndLoad',
      width: '%15',
      render: (fileLink) =>
        fileLink && (
          <Typography.Link href={fileLink}>
            {t('scenario.table.pricesAndLoadFile')}
          </Typography.Link>
        ),
    },
    {
      title: t('scenario.table.creationDate'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '%15',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (data) => (
        <Row align="middle">
          <Typography.Paragraph
            style={{ maxWidth: '200px' }}
            className="text"
            ellipsis={{
              rows: 1,
              tooltip: data,
              expandable: false,
            }}
          >
            {dayjs(data).format('DD.MM.YYYY HH:MM')}
          </Typography.Paragraph>
        </Row>
      ),
    },
    {
      title: t('scenario.table.createdBy'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: '%15',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (data) => (
        <Row align="middle">
          <Typography.Paragraph
            style={{ maxWidth: '200px' }}
            className="text"
            ellipsis={{
              rows: 1,
              tooltip: data,
              expandable: false,
            }}
          >
            {data}
          </Typography.Paragraph>
        </Row>
      ),
    },
    {
      title: (
        <CustomTooltip
          className="cursor-pointer"
          title={t('scenario.table.tooltip.statusTitle')}
          description={t('scenario.table.tooltip.statusDesc')}
          trigger="click"
        >
          <Row align="middle" justify="start" className="w-100">
            <Typography.Text className="pr-8">
              {t('scenario.table.status')}
            </Typography.Text>
            <InfoIcon fill={theme.colors.dark} />
          </Row>
        </CustomTooltip>
      ),
      sorter: (a, b) => String(a.isActive).localeCompare(String(b.isActive)),
      dataIndex: 'isActive',
      key: 'isActive ',
      align: 'left',
      width: '%10',
      render: (dataIndex) =>
        dataIndex ? <RTag type="active" /> : <RTag type="inactive" />,
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions ',
      width: '60px',
      render: (_, record: Scenario, index: number) => (
        <>
          {contextHolder}
          <div
            ref={index === 0 ? dropdownWrapperRef : undefined}
            style={{
              width: '32px',
              height: '32px',
            }}
          >
            <Dropdown
              trigger={['click']}
              menu={{
                items: generateItems(record),
                onClick: (info) => handleMenuClick(info, record),
              }}
              open={
                index === 0 &&
                isTourActive &&
                currentStep === TourSteps.VIEW_SCENARIOS
                  ? true
                  : undefined
              } // Menu will automatically open when tour is active
              placement="bottomRight"
            >
              <Button
                className="default-icon-button"
                type="default"
                icon={<VerticalEllipsisIcon fill={theme.colors.bluePrimary} />}
              />
            </Dropdown>
          </div>
        </>
      ),
    },
  ];

  return columns;
};

export default useScenariosTableColumns;
